import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { PreferenceService } from 'app/core/services/preference/preference.service';
import { ThemeService } from 'app/shared/theme/theme.service';
import { PRIMECONTROL_BUTTONS } from 'app/shared/buttons/buttons';

import { CommonModule } from '@angular/common';
import { DIVIDER_LINE } from 'app/shared/divider-line/divider-line';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { PcFullscreenComponent } from 'app/shared/components/fullscreen';
import { MenuComponent } from 'app/shared/molecules/menu';
import { PcConfigService } from 'app/shared/services/config';
import { Scheme } from 'app/core/config/app.config';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.css',
  imports: [
    PRIMECONTROL_BUTTONS,
    MenuComponent,
    CommonModule,
    DIVIDER_LINE,
    SvgIcon,
    TranslateModule,
    PcFullscreenComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
})
export class UserComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar: boolean = true;
  user: User;
  currentScheme: Scheme;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private _userService: UserService,
    private _authService: AuthService,
    private _preferenceService: PreferenceService,
    private _pcConfigService: PcConfigService,
    private _themeService: ThemeService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.user = JSON.parse(this._userService.user$);

    // Load saved scheme from localStorage or default to 'light'
    const savedScheme = localStorage.getItem('pc_scheme') as Scheme;
    if (savedScheme) {
      this.currentScheme = savedScheme;
      this._pcConfigService.config = { scheme: savedScheme };
    }

    // Subscribe to config changes to track current scheme
    this._pcConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.currentScheme = config.scheme;
      this._changeDetectorRef.markForCheck();
    });

    this._changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /** Display name for the header trigger; falls back to the email's local part. */
  get fullName(): string {
    const name = [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' ');
    return name || this.user?.email?.split('@')[0] || '';
  }

  /** Avatar initials from first/last name, falling back to the email. */
  get initials(): string {
    const first = this.user?.firstName?.charAt(0) || '';
    const last = this.user?.lastName?.charAt(0) || '';
    return first + last || this.user?.email?.charAt(0) || '?';
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the user status
   *
   * @param status
   */
  updateUserStatus(status: string): void {
    // Return if user is not available
    if (!this.user) {
      return;
    }

    // Update the user
    this._userService
      .update({
        ...this.user,
        status,
      })
      .subscribe();
  }

  /**
   * Sign out
   */
  signOut(): void {
    this._authService.signOut().subscribe(() => {
      this._router.navigate(['auth/sign-in']);
    });
  }

  /** Base mode signal from the REAL theme system — drives the selector state. */
  get baseMode() {
    return this._themeService.theme;
  }

  setScheme(scheme: Scheme): void {
    // Drive ThemeService (owner of <html>/<body> classes, PrimeNG preset and
    // persistence). The legacy PcConfig/localStorage writes stay for old readers.
    this._themeService.switchBaseMode(scheme === 'auto' ? 'system' : scheme);
    this.currentScheme = scheme;
    this._pcConfigService.config = { scheme };
    localStorage.setItem('pc_scheme', scheme);
  }

  selectorInstance() {
    this._router.navigate(['select-instance']);
  }

  goToPreferences(): void {
    this._router.navigate(['profile/preferences']);
  }

  goToSecurity(): void {
    this._router.navigate(['profile/security']);
  }

  goToOrganizations(): void {
    this._router.navigate(['profile/organizations']);
  }
}
